import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import PTAFormField from 'Components/PTAFormField'
import StyledForm from 'Components/StyledForm/StyledForm'
import WithLoader from 'Components/WithLoader/WithLoader'
import AllowedApiClients from 'Services/common/Features/AllowedApiClient/Components/AllowedApiClients'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import {
  useCreateSingleDeviceMutation,
  useUpdateSingleDeviceMutation
} from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import type { Device } from 'Services/ServiceProviderManagement/Features/Device/types'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import { SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Pages/SalesPointListPage'
import { useGetAllSalesPointsQuery } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import { SalesPointTabType } from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import type { FieldValidationErrors } from 'Services/ServiceProviderManagement/types'
import useTypedSelector from 'Store'
import type { ServiceProviderManagementError } from 'Util/ApiClient'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import {
  ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  Service,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC
} from 'Util/constants'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Autocomplete, Box, FormHelperText, Grid, MenuItem, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  device_id: [],
  name: [],
  sales_point_id: [],
  source: [],
  directory_alias: [],
  machine_uuid: []
}

interface DeviceFormProps {
  device?: Device
  successBaseUrl: string
}

export default function DeviceForm({ device, successBaseUrl }: Readonly<DeviceFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!device
  const { user } = useTypedSelector(state => state.user)

  const [createSingleDevice, createStatus] = useCreateSingleDeviceMutation()
  const [updateSingleDevice, updateStatus] = useUpdateSingleDeviceMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [searchParams] = useSearchParams()
  const selectedSalesPointId = searchParams.get(SELECTED_SALES_POINT_PARAMETER_KEY)

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  const { selectedAuthorityCode } = useAuthorityContext()

  const deviceDefaultValues: Partial<Device> = {
    pta: IS_WALTTI_SERVICE_ENVIRONMENT ? (selectedAuthorityCode ?? undefined) : undefined,
    sales_point_id: selectedSalesPointId ?? undefined,
    active: true
  }

  const { getCopiedObject, clearCopiedObject } = useCopyObjectContext()

  const copiedDeviceData = isEdit ? null : getCopiedObject<Device>(CopyObjectKeys.DEVICE)
  const [deviceDetails, setDeviceDetails] = useState<Partial<Device>>(
    (isEdit && device) || copiedDeviceData || { ...deviceDefaultValues }
  )

  const {
    data: { resources: allSalesPoints } = { resources: [] },
    isLoading: isAllSalesPointsLoading
  } = useGetAllSalesPointsQuery(
    {
      sort_by: 'name',
      sort_order: SORT_ORDER_ASC,
      pta: deviceDetails.pta ?? undefined
    },
    { refetchOnMountOrArgChange: true }
  )

  const salesPointOptions = allSalesPoints.map(salesPoint => {
    return {
      value: salesPoint.sales_point_id,
      label: salesPoint.name
    }
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress clearCopiedObject, copiedDeviceData
  useEffect(() => {
    if (copiedDeviceData) {
      clearCopiedObject(CopyObjectKeys.DEVICE)
    }
  }, [])

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    deviceDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    deviceDetails?.client_write ?? []
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined | null,
    field: keyof Device
  ) => {
    if (value !== undefined) {
      setDeviceDetails(prevState => ({ ...prevState, [field]: value }) as Device)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const deviceData = {
      ...deviceDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleDevice({
          ...deviceData,
          deviceId: device.device_id
        })
      : createSingleDevice({
          ...deviceData,
          pta: IS_HSL_SERVICE_ENVIRONMENT ? user?.pta : deviceData.pta
        })

    mutation
      .unwrap()
      .then((deviceResponse: Device) => {
        SnackbarUtils.success(t`Device successfully saved.`)
        if (selectedSalesPointId) {
          navigate(
            appendQueryParamsToUrl(`${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`, {
              [SELECTED_SALES_POINT_PARAMETER_KEY]: selectedSalesPointId,
              [PTA_PARAM_KEY]: deviceResponse.pta,
              [SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY]: SalesPointTabType.DEVICES
            })
          )
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_DEVICE_PARAMETER_KEY]: deviceResponse.device_id,
              [PTA_PARAM_KEY]: deviceResponse.pta
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the device.`)
      })
  }

  return (
    <WithLoader loading={isAllSalesPointsLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && (
              <Box>
                <PTAFormField
                  value={deviceDetails.pta}
                  onChange={value => handleChange(value, 'pta')}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <FormTextField
                  name="name"
                  required
                  label={t`Name`}
                  value={deviceDetails?.name ?? ''}
                  maxLength={40}
                  onChange={e => handleChange(e.target.value, 'name')}
                  {...getErrorProps('name')}
                />
              </Box>
              <Box>
                <FormTextField
                  name="device_id"
                  required
                  label={t`Device id`}
                  value={deviceDetails?.device_id ?? ''}
                  maxLength={9}
                  onChange={e => handleChange(e.target.value, 'device_id')}
                  {...getErrorProps('device_id')}
                />
              </Box>
              <Box>
                <Autocomplete
                  disablePortal
                  size="small"
                  options={salesPointOptions}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.value}>
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={params => (
                    <TextField required {...params} label={t`Device's sales point`} />
                  )}
                  onChange={(_, newValue) =>
                    handleChange(newValue?.value as string, 'sales_point_id')
                  }
                  value={
                    salesPointOptions.find(
                      option => option.value === deviceDetails.sales_point_id
                    ) || null
                  }
                  sx={{
                    minWidth: 240
                  }}
                />
                <FormHelperText error={fieldErrors.sales_point_id.length > 0}>
                  {getFieldErrorHelperText('sales_point_id')}
                </FormHelperText>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <FormTextField
                  name="source"
                  label={t`Source`}
                  value={deviceDetails?.source ?? ''}
                  onChange={e => handleChange(e.target.value, 'source')}
                  maxLength={256}
                  {...getErrorProps('source')}
                />
              </Box>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <>
                  <Box>
                    <FormTextField
                      name="directory_alias"
                      label={t`Directory alias`}
                      value={deviceDetails?.directory_alias ?? ''}
                      onChange={e => handleChange(e.target.value, 'directory_alias')}
                      maxLength={256}
                      {...getErrorProps('directory_alias')}
                    />
                  </Box>
                  <Box>
                    <FormTextField
                      name="machine_uuid"
                      label={t`Machine uuid`}
                      value={deviceDetails?.machine_uuid ?? ''}
                      onChange={e =>
                        handleChange(e.target.value === '' ? null : e.target.value, 'machine_uuid')
                      }
                      maxLength={256}
                      {...getErrorProps('machine_uuid')}
                    />
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ maxWidth: ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH, py: 2 }}>
              <AllowedApiClients
                service={Service.SERVICE_PROVIDER_MANAGEMENT}
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save device`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
